import clsx from "clsx";
import React, { FC, memo, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import FileLoader from "~/components/FileLoader";
import Checkbox from "~/shared/ui/Fields/Checkbox";
import DatesPicker from "~/shared/ui/Fields/DatePicker";
import Input from "~/shared/ui/Fields/Input";
import Select from "~/shared/ui/Fields/Select";
import TextArea from "~/shared/ui/Fields/TextArea";
import { newSelectStyles } from "~/components/Forms/DealerPassportForm2/options";

import styles from "./styles.less";

export interface FieldConfig {
  name: string;
  label: string;
  type:
    | "text"
    | "number"
    | "phone"
    | "percent"
    | "email"
    | "select"
    | "date"
    | "file"
    | "checkbox"
    | "hidden"
    | "textarea"
    | "boolean";
  options?: string[] | { value: any; label: string }[];
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  customClassName?: string;
  newStyle?: boolean;
  className?: string;
  loading?: boolean;
  defaultValue?: any;
  mask?: string;
  getEventName?: (name: string) => void;
  ownerIndex?: number;
  ownerErrors?: any;
  ownerName?: any;
}

const DynamicFormField: FC<FieldConfig> = ({
  name,
  label,
  type,
  options = [],
  disabled = false,
  required = false,
  placeholder,
  customClassName,
  newStyle = true,
  className,
  loading = false,
  defaultValue,
  mask,
  getEventName,
  ownerIndex,
  ownerErrors,
  ownerName,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();

  const isDisabled = disabled || loading;

  useEffect(() => {
    const currentValue = getValues(name);
    if (defaultValue !== undefined && currentValue === undefined) {
      setValue(name, defaultValue, { shouldValidate: false });
    }
  }, [defaultValue, name, setValue, getValues]);

  const normalizedOptions = useMemo(() => {
    if (Array.isArray(options) && typeof options[0] === "string") {
      return (options as string[]).map((option) => ({
        value: option,
        label: option,
      }));
    }
    return options as { value: any; label: string }[];
  }, [JSON.stringify(options)]);

  const renderField = useMemo(() => {
    switch (type) {
      case "text":
      case "number":
      case "email":
      case "hidden":
        const error = ownerErrors?.[ownerIndex]?.[ownerName]?.message as string;
        return (
          <Input
            name={name}
            label={label}
            control={control}
            type={type}
            disabled={isDisabled}
            error={
              ownerErrors && !!error ? error : (errors[name]?.message as string)
            }
            placeholder={placeholder}
            newStyle={newStyle}
            customClassName={customClassName}
            defaultValue={""}
            mask={mask}
          />
        );
      case "phone":
        return (
          <Input
            name={name}
            label={label}
            control={control}
            disabled={isDisabled}
            error={
              ownerErrors && !!error ? error : (errors[name]?.message as string)
            }
            mask="+7 (999) 999-99-99"
            placeholder={placeholder}
            newStyle={newStyle}
            customClassName={customClassName}
          />
        );

      case "select":
        return (
          <Select
            name={name}
            label={label}
            control={control}
            type="dealersForm"
            options={normalizedOptions}
            error={errors[name]?.message || errors[name]?.label?.message}
            customClassName={customClassName}
            newStyle={newStyle}
            disabled={isDisabled}
            loading={loading}
            newCustomClassNames={newSelectStyles}
            getEventName={getEventName}
          />
        );
      case "textarea":
        return (
          <TextArea
            name={name}
            label={label}
            control={control}
            type={type}
            disabled={isDisabled}
            error={errors[name]?.message as string}
            placeholder={placeholder}
            newStyle={newStyle}
            customClassName={customClassName}
          />
        );

      case "date":
        return (
          <div className={styles.datepickerContainer}>
            <DatesPicker
              name={name}
              dealerTitle={label}
              control={control}
              gray
              disabled={isDisabled}
              customClassName={clsx(customClassName, styles.datepicker)}
              dealerTitleClassName={styles.datepickerTitle}
              dateFormat="dd.MM.yyyy"
              error={errors[name]?.message as string}
            />
          </div>
        );

      case "file":
        return (
          <FileLoader
            name={name}
            label={label}
            control={control}
            withButton
            isNoneUpload={isDisabled}
          />
        );

      case "checkbox":
        return (
          <Select
            name={name}
            label={label}
            control={control}
            customClassName={customClassName}
            optionObjects={[
              { value: 0, label: "Нет" },
              { value: 1, label: "Да" },
            ]}
            error={errors[name]?.message as string}
            disabled={isDisabled}
            newStyle={newStyle}
            newCustomClassNames={newSelectStyles}
          />
        );
      case "boolean":
        return (
          <div className={styles.booleanWrapper}>
            <span className={styles.booleanLabel}>{label}</span>
            <Controller
              name={name}
              control={control}
              render={({ onChange, value }) => (
                <Checkbox
                  disabled={isDisabled}
                  checked={!!value}
                  onCheck={(val) => onChange(val)}
                />
              )}
            />
          </div>
        );

      default:
        return null;
    }
  }, [
    name,
    label,
    type,
    JSON.stringify(normalizedOptions),
    disabled,
    loading,
    errors[name],
    control,
    placeholder,
    newStyle,
    customClassName,
    ownerErrors,
    ownerIndex,
    ownerName,
    mask,
  ]);

  return <div className={clsx(className)}>{renderField}</div>;
};

export default memo(DynamicFormField, (prevProps, nextProps) => {
  return (
    prevProps.name === nextProps.name &&
    prevProps.label === nextProps.label &&
    prevProps.type === nextProps.type &&
    JSON.stringify(prevProps.options) === JSON.stringify(nextProps.options) &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.required === nextProps.required &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.customClassName === nextProps.customClassName &&
    prevProps.newStyle === nextProps.newStyle &&
    prevProps.className === nextProps.className &&
    prevProps.loading === nextProps.loading &&
    JSON.stringify(prevProps.defaultValue) ===
      JSON.stringify(nextProps.defaultValue) &&
    prevProps.ownerErrors === nextProps.ownerErrors &&
    prevProps.getEventName === nextProps.getEventName &&
    prevProps.ownerName === nextProps.ownerName &&
    prevProps.mask === nextProps.mask
  );
});
